import ModalViewModel, {
  ModalViewModelClosingSignal,
} from 'infra/ModalViewModel'
import Job from 'jobs/Job'
import link from 'infra/link'
import { Alert, AlertFactory, AlertState } from 'alerts/Alert'
import { CopyJob } from 'jobs/JobStore'
import { noop, once } from 'lodash'
import { Id } from 'types/definitions'
import { CancellationToken } from 'infra/cancellation'

/**
 * Copy Job view model.
 */
export default class CopyJobViewModel extends ModalViewModel {
  /**
   * Constructor.
   *
   * @param job
   * @param newAlert
   * @param copyJobDelegate
   * @param closingSignal
   */
  constructor(
    public job: Job,
    private newAlert: AlertFactory,
    private copyJobDelegate: CopyJob,
    closingSignal?: ModalViewModelClosingSignal,
  ) {
    super(closingSignal)
  }

  /**
   * Copies the job and returns the ID of the new job.
   */
  async _copyJob() {
    const alert = this.newAlert()
      .show()
      .withState(AlertState.Pending)
      .saying('Copying job...')

    try {
      const copiedJob = await this.copyJobDelegate(this.job)
      if (copiedJob.readyForSubmission) {
        alert
          .withState(AlertState.Success)
          .saying('Job copied! Please review the job and submit it.')
          .autoDismiss()
      } else {
        alert
          .withState(AlertState.Warning)
          .saying(
            'Job copied though some questions have changed! Please review the job before attempting to submit it.',
          )
          .autoDismiss()
      }

      return Id.assert(copiedJob.jobId)
    } catch (error) {
      alert.fromError(error)
      throw error
    }
  }

  /**
   * Copies the current job.
   */
  copyJob = async () => {
    return link.job(await this._copyJob())
  }

  /**
   * Default instance.
   */
  static get none() {
    return lazyNone()
  }
}

const lazyNone = once(
  () =>
    new CopyJobViewModel(
      Job.none,
      () => new Alert(noop, noop),
      (job: Job) =>
        Promise.resolve({
          jobId: Id.assert(job.id),
          readyForSubmission: false,
        }),
      CancellationToken.cancelled,
    ),
)
